import * as React from "react";
import { useEffect } from "react";
import styled from "styled-components";
import { useFormikContext } from "formik";
import { Spinner } from "components/common";
import { Alert } from "components/bootstrap";
import { Col, Row } from "components/lookup-tables/layout-componets";
import { ErrorsProvider } from "components/lookup-tables/contexts/ErrorsContext";
import ErrorsConsumer from "components/lookup-tables/lookup-table-list/errors-consumer";
import LookupTableDetails from "components/lookup-tables/lookup-table-view/basic-details";
import PurgeCache from "components/lookup-tables/lookup-table-view/purge-cache";
import TestLookup from "components/lookup-tables/lookup-table-view/test-lookup";
import { useFetchCache, useFetchDataAdapter } from "components/lookup-tables/hooks/useLookupTablesAPI";
import Cache from "components/lookup-tables/Cache";
import DataAdapter from "components/lookup-tables/DataAdapter";
import useDefaultValueValidation from "./use-default-value-validation";
const StyledContainer = styled(Row)`
  max-width: 1500px;
  margin: 0 auto;

  & .content.row {
    width: 100%;
    margin: 0;
    padding: 0;
    box-shadow: none;

    [class*='col-'] {
      padding: 0;
      margin: 0;
    }
  }
`;
const DangerText = styled.i`
  color: ${({ theme }) => theme.colors.variant.danger};
`;
const MuttedText = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: break-word;
  width: 50px;
`;
function DefaultValueErrors({ errors }) {
  const invalidDefaults = [
    ["Default single value", errors.default_single_value],
    ["Default multi value", errors.default_multi_value]
  ].filter(([, error]) => !!error);
  if (invalidDefaults.length === 0) return null;
  return /* @__PURE__ */ React.createElement(Alert, { style: { width: "100%" }, bsStyle: "danger", title: "Invalid default values" }, invalidDefaults.map(([label, error]) => /* @__PURE__ */ React.createElement("div", { key: label }, label, ": ", error)), /* @__PURE__ */ React.createElement("div", null, "Fix them on the Lookup Table step."));
}
function TableSummary({ table, errors }) {
  return /* @__PURE__ */ React.createElement(Col, { $gap: "lg" }, /* @__PURE__ */ React.createElement(Col, { $gap: "sm" }, /* @__PURE__ */ React.createElement("h2", null, "Lookup Table Details"), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(MuttedText, null, "Title:"), /* @__PURE__ */ React.createElement("span", null, table.title || /* @__PURE__ */ React.createElement(DangerText, null, "No title set (required)"))), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(MuttedText, null, "Name:"), /* @__PURE__ */ React.createElement("span", null, table.name || /* @__PURE__ */ React.createElement(DangerText, null, "No name set (required)")))), /* @__PURE__ */ React.createElement(LookupTableDetails, { table }), /* @__PURE__ */ React.createElement(DefaultValueErrors, { errors }), table.id && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(PurgeCache, { table }), /* @__PURE__ */ React.createElement(TestLookup, { table })));
}
function CacheSummary({ cache }) {
  if (!cache)
    return /* @__PURE__ */ React.createElement(Alert, { style: { width: "100%" }, bsStyle: "danger" }, "No cache selected.");
  return /* @__PURE__ */ React.createElement(Cache, { cache, noEdit: true });
}
function AdapterSummary({ adapter }) {
  if (!adapter)
    return /* @__PURE__ */ React.createElement(Alert, { style: { width: "100%" }, bsStyle: "danger" }, "No data adapter selected.");
  return /* @__PURE__ */ React.createElement(DataAdapter, { dataAdapter: adapter, noEdit: true });
}
function SummaryStep() {
  const { values, initialValues } = useFormikContext();
  const { defaultValueErrors, validateDefaultValues } = useDefaultValueValidation();
  const { cache, loadingCache } = useFetchCache(values.cache_id);
  const { dataAdapter, loadingDataAdapter } = useFetchDataAdapter(values.data_adapter_id);
  useEffect(() => {
    validateDefaultValues();
  }, []);
  return /* @__PURE__ */ React.createElement(ErrorsProvider, null, /* @__PURE__ */ React.createElement(
    ErrorsConsumer,
    {
      lutNames: initialValues.id && initialValues.name ? [initialValues.name] : void 0,
      cacheNames: (cache == null ? void 0 : cache.name) ? [cache.name] : void 0,
      adapterNames: (dataAdapter == null ? void 0 : dataAdapter.name) ? [dataAdapter.name] : void 0
    }
  ), /* @__PURE__ */ React.createElement(StyledContainer, { $gap: "xl" }, /* @__PURE__ */ React.createElement(TableSummary, { table: values, errors: defaultValueErrors }), /* @__PURE__ */ React.createElement(Col, { $gap: "lg" }, loadingCache ? /* @__PURE__ */ React.createElement(Spinner, { text: "Loading cache..." }) : /* @__PURE__ */ React.createElement(Col, { $gap: "sm" }, /* @__PURE__ */ React.createElement("h2", null, "Cache Details"), /* @__PURE__ */ React.createElement(CacheSummary, { cache })), loadingDataAdapter ? /* @__PURE__ */ React.createElement(Spinner, { text: "Loading data adapter..." }) : /* @__PURE__ */ React.createElement(Col, { $gap: "sm" }, /* @__PURE__ */ React.createElement("h2", null, "Data Adapter Details"), /* @__PURE__ */ React.createElement(AdapterSummary, { adapter: dataAdapter })))));
}
export default SummaryStep;
